import { useState, useCallback, useEffect, useRef } from "react";
import { useTournamentData } from "./useTournamentData";
import { useScoreManagement } from "./useScoreManagement";
import { useCalculationEngine } from "./useCalculationEngine";
import { useTournamentFilters } from "./useTournamentFilters";
import { useTournamentVerification } from "../../../hooks/useTournamentVerification";
import { attachLaneFields } from "../../../utility/laneAssignmentApi";
import { attachRecapSheetFields } from "../../../utility/recapSheets";
import { loadScoringHubData } from "../../../utility/loadScoringHubData";

export const useUnifiedTeamData = (tournamentId, filters = {}) => {
  // Core data management
  const {
    data,
    allTeams,
    loading,
    error,
    refetch,
    calculationResults: initialCalculationResults,
  } = useTournamentData(tournamentId);

  // Use the new verification system
  const {
    verificationStatus,
    loading: verificationLoading,
    error: verificationError,
    canVerifyTeam,
    getTeamVerificationStatus,
  } = useTournamentVerification(tournamentId);

  // Local state for data updates
  const [localData, setLocalData] = useState(data);

  // Ref to always have access to latest localData in closures
  const localDataRef = useRef(localData);

  // Keep ref in sync with state
  useEffect(() => {
    localDataRef.current = localData;
  }, [localData]);

  // Score management
  const { updateScore, isScoreSaving, clearAllPendingSaves } =
    useScoreManagement(tournamentId, localData, setLocalData);

  // Calculation engine - pass initial results
  const {
    calculatingTotals,
    getCalculationResult,
    getCalculationError,
    clearCalculations,
    calculateTeamScores,
  } = useCalculationEngine(tournamentId, initialCalculationResults);

  // Filtering - use localData instead of data to get updated verification status
  const { filteredData, clearAllFilters } = useTournamentFilters(
    localData,
    filters
  );

  // Local state for filtered data
  const [filteredTeams, setFilteredTeams] = useState([]);

  // Flag to prevent filter effect from overriding optimistic updates
  const [isUpdatingVerification, setIsUpdatingVerification] = useState(false);

  // Sync local data with fetched data
  useEffect(() => {
    if (data) {
      setLocalData(data);
    }
  }, [data]);

  // Apply filters when data or filters change
  useEffect(() => {
    // Skip filter updates when we're in the middle of a verification update
    if (isUpdatingVerification) {
      return;
    }

    if (filteredData?.teams) {
      setFilteredTeams(filteredData.teams);
    }
  }, [filteredData, isUpdatingVerification]);

  // Reset verification update flag after a short delay
  useEffect(() => {
    if (isUpdatingVerification) {
      const timer = setTimeout(() => {
        setIsUpdatingVerification(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isUpdatingVerification]);

  // Cleanup effect to clear any stuck loading states when component unmounts
  useEffect(() => {
    return () => {
      clearAllPendingSaves();
    };
  }, [clearAllPendingSaves]);

  // Note: Verification status sync removed - we rely on optimistic updates
  // The verificationStatus is only used for initial load and permission checks

  // Enhanced score update that triggers calculations
  const handleScoreUpdate = useCallback(
    async (teamId, bowlerId, gameNumber, score) => {
      try {
        // Save the score
        await updateScore(teamId, bowlerId, gameNumber, score);

        // Find the team data for calculations
        const team = localData?.teams?.find((t) => t.id === teamId);

        if (team) {
          // CRITICAL: Create a copy of the team data with the updated score
          // This ensures calculations use the most current data
          const updatedTeam = {
            ...team,
            transformedScores: {
              ...team.transformedScores,
              [bowlerId]:
                team.transformedScores?.[bowlerId]?.map((scoreObj, index) => {
                  if (index === gameNumber) {
                    return { ...scoreObj, score: score };
                  }
                  return scoreObj;
                }) || [],
            },
          };

          // Create a function that gets the latest team data from localDataRef
          // This ensures queued calculations always use the most current data
          // Using ref so closure always reads latest value
          const getLatestTeamData = () => {
            const latestTeam = localDataRef.current?.teams?.find(
              (t) => t.id === teamId
            );
            if (!latestTeam) {
              return updatedTeam; // Fallback to the updated team we created
            }
            // Return the latest team data directly (it should already have the updated score from updateScore)
            return latestTeam;
          };

          // Trigger calculations with the updated data and a function to get latest data
          await calculateTeamScores(teamId, updatedTeam, getLatestTeamData);
        }
      } catch (error) {
        console.error("Error in handleScoreUpdate:", error);
        throw error;
      }
    },
    [updateScore, calculateTeamScores, localData]
  );

  // Function to remove a team from local state (for optimistic updates)
  const removeTeamFromState = useCallback(
    (teamId) => {
      setLocalData((prevData) => ({
        ...prevData,
        teams: prevData.teams.filter((team) => team.id !== teamId),
      }));

      // Clear calculations for the deleted team
      clearCalculations(teamId);
    },
    [clearCalculations]
  );

  const patchTeamFields = useCallback((teamId, fields) => {
    setIsUpdatingVerification(true);

    const apply = (list) =>
      (list || []).map((team) =>
        team.id === teamId ? { ...team, ...fields } : team
      );

    setLocalData((prevData) => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        teams: apply(prevData.teams),
      };
    });

    setFilteredTeams((prevFiltered) => apply(prevFiltered));
  }, []);

  // Function to update team verification status without clearing calculations
  const updateTeamVerification = useCallback((teamId, isVerified) => {
    // Set flag to prevent filter effect from overriding our update
    setIsUpdatingVerification(true);

    // Update local state immediately for better UX
    setLocalData((prevData) => {
      const updatedTeams = prevData.teams.map((team) =>
        team.id === teamId
          ? {
              ...team,
              isVerified,
              verifiedAt: isVerified ? new Date().toISOString() : null,
              verifiedBy: isVerified ? null : null,
            }
          : team
      );

      return {
        ...prevData,
        teams: updatedTeams,
      };
    });

    // Update filtered teams immediately for instant UI feedback
    setFilteredTeams((prevFiltered) => {
      const updatedFiltered = prevFiltered.map((team) =>
        team.id === teamId
          ? {
              ...team,
              isVerified,
              verifiedAt: isVerified ? new Date().toISOString() : null,
              verifiedBy: isVerified ? null : null,
            }
          : team
      );

      return updatedFiltered;
    });

    // Clear the flag after a short delay to allow filter effect to work again
    setTimeout(() => {
      setIsUpdatingVerification(false);
    }, 100);
  }, []);

  // Helper function to check if a team can be verified using the new system
  const canVerifyTeamForEdit = useCallback(
    (teamId) => {
      if (verificationStatus) {
        return canVerifyTeam(teamId);
      }
      // Fallback to old system - check if team is not verified
      const team = data?.teams?.find((t) => t.id === teamId);
      return team && !team.isVerified;
    },
    [verificationStatus, canVerifyTeam, data]
  );

  // Function to add a new team to the local data without triggering a full refresh
  const addTeamToState = useCallback(
    async (newTeam) => {
      // Validate that the team has a valid ID
      if (!newTeam.id) {
        return;
      }

      // Add a placeholder team with loading state
      const placeholderTeam = {
        ...newTeam,
        isLoading: true,
        bowlers: [], // Empty bowlers array to show loading state
        teamName: newTeam.teamName || "Loading...", // Show loading text
      };

      setLocalData((prevData) => {
        if (!prevData) return prevData;

        // Check if team already exists to prevent duplicates
        const teamExists = prevData.teams.some(
          (team) => team.id === newTeam.id
        );
        if (teamExists) {
          return prevData;
        }

        return {
          ...prevData,
          teams: [...(prevData.teams || []), placeholderTeam],
        };
      });

      // Then fetch the full team data with bowlers
      try {
        const invoked = await loadScoringHubData(tournamentId);
        const fullTeamData = await attachRecapSheetFields(
          await attachLaneFields(invoked)
        );

        // Find the specific team we just created
        if (
          fullTeamData &&
          fullTeamData.teams &&
          fullTeamData.teams.length > 0
        ) {
          const fullTeam = fullTeamData.teams.find(
            (team) => team.id === newTeam.id
          );

          if (!fullTeam) {
            return;
          }

          const updatedTeam = {
            ...fullTeam,
            isLoading: false, // Mark as loaded
          };

          setLocalData((prevData) => {
            if (!prevData) return prevData;

            return {
              ...prevData,
              teams: prevData.teams.map((team) =>
                team.id === newTeam.id ? updatedTeam : team
              ),
            };
          });
        }
      } catch (error) {
        // Remove loading state on error
        setLocalData((prevData) => {
          if (!prevData) return prevData;

          return {
            ...prevData,
            teams: prevData.teams.map((team) =>
              team.id === newTeam.id ? { ...team, isLoading: false } : team
            ),
          };
        });
      }
    },
    [tournamentId, localData?.teams?.length]
  );

  return {
    // Data
    data: localData,
    teams: filteredTeams,
    allTeams,
    loading: loading || verificationLoading,
    error: error || verificationError,

    // Actions
    updateScore: handleScoreUpdate,
    refetch,
    removeTeamFromState,
    updateTeamVerification,
    patchTeamFields,
    addTeamToState,

    // Calculations
    calculatingTotals,
    getCalculationResult,
    getCalculationError,
    clearCalculations,

    // Filters
    filters,
    clearAllFilters,

    // Computed values
    hasData: !!localData?.teams && localData.teams.length > 0,
    teamsCount: filteredTeams.length,

    // Verification helpers
    canVerifyTeamForEdit,
    verificationStatus,

    // Score saving status
    isScoreSaving,
  };
};
